import Layer from '@arcgis/core/layers/Layer';
import { AppConstants } from 'cd-eft/appConstants';
import {
  ElevationInterpolater,
  getDistance2D,
} from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/elevationInterpolationEngine';
import { IDisposable } from 'cd-eft/framework/Disposer';
import { XyzPoint } from 'cd-eft/models/SurveyEngineering/XyzPoint';
import {
  esriGeometryEngine,
  esriProjection,
  Extent,
  FeatureLayer,
  Geometry,
  Graphic,
  GraphicsLayer,
  MapView,
  Point,
  Polyline,
  SpatialReference,
} from 'cd-eft/utils/esriTypes';
import { action } from 'mobx';
import { ProfileKeyCode } from './DynamicProfileEnums';
import stores from '..';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import * as profileSymbolsAndLabels from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';

const designStore = stores.AppStore.designStore;
type componentType = 'alignment' | 'singleCrossSection' | 'waterway' | 'threeCrossSection1' | 'threeCrossSection2' | 'threeCrossSection3' | 'terrace' | 'UGO';

class DynamicProfileClass implements IDisposable {
  private profileKey: ProfileKeyCode = ProfileKeyCode.DEFAULT;
  private _designProfileLayer: GraphicsLayer | undefined;
  private _groundProfileLayer: GraphicsLayer | undefined;
  private _curvedProfileLayer: GraphicsLayer | undefined;

  private _profileView: MapView;
  private h_grid: FeatureLayer | undefined = void 0;
  private v_grid: FeatureLayer | undefined = void 0;
  public maxExtent: Extent | undefined = void 0;

  private mapSpatialReference: SpatialReference | undefined = void 0;
  private dataSpatialReference: SpatialReference | undefined = void 0;

  /** profile data */
  designVertices = [] as number[][];
  public groundProfilePoints = [] as number[][];
  private designProfilePoints = [] as number[][];
  private designVertexQueue = [] as number[][];
  private profileBatchDrawQueue = [] as number[][];

  private curveVertices = [] as number[][];
  private curveGroundProfilePoints = [] as number[][];
  private curvedProfilePoints = [] as number[][];

  public maxElevation = -1000;
  public minElevation = 1000000;
  private componentType: componentType;

  public defaultDesignProfileColor = 'red';
  public defaultGroundProfileColor: string | number[];
  public defaultGroundProfileStyle: string;
  public defaultGroundProfileWidth: number = 2;
  public defaultCurvedProfileColor = 'green';

  public ymax: number | undefined;
  public ymin: number | undefined;

  private beginningStation = 0;
  public waterlevelShiftRightValue: number = 0;
  public designProfileStartDist: number = 0;
  public tcsBufferWidth: number = 0;
  /**
   *
   */
  constructor(
    profileView: MapView,
    profileKey: ProfileKeyCode,
    dataSr: SpatialReference,
    mapSr: SpatialReference,
    h_grid: FeatureLayer,
    v_grid: FeatureLayer,
    componentType: componentType
  ) {
    this._profileView = profileView;
    this.maxExtent = profileView.extent;

    this.h_grid = h_grid;
    this.v_grid = v_grid;

    this.profileKey = profileKey;
    this.dataSpatialReference = dataSr;
    this.mapSpatialReference = mapSr;

    this.componentType = componentType;

    // Set profile color and style
    switch (profileKey) {
      case 2: // cross section original profile
        this.defaultGroundProfileColor = profileSymbolsAndLabels.netsLegendPink;
        this.defaultGroundProfileStyle = 'solid';
        break;
      case 3: // cross section design profile
      this.defaultGroundProfileColor = profileSymbolsAndLabels.netsLegendOrange;
      this.defaultGroundProfileStyle = 'solid';
        break;
      case 4: // waterway centerline
        this.defaultGroundProfileColor = profileSymbolsAndLabels.netsLegendBlue;
        this.defaultGroundProfileStyle = 'short-dash-dot';
        break;
      case 5: // waterway low bank
        this.defaultGroundProfileColor = profileSymbolsAndLabels.netsLegendPink;
        this.defaultGroundProfileStyle = 'dot';
        break;
      case 6: // waterway right bank
        this.defaultGroundProfileColor = 'red';
        this.defaultGroundProfileStyle = 'dot';
        break;
      case 7: // waterway left bank
        this.defaultGroundProfileColor = profileSymbolsAndLabels.netsLegendLightGreen;
        this.defaultGroundProfileStyle = 'dot';
        break;
      case 9: // water level
        this.defaultGroundProfileColor = profileSymbolsAndLabels.netsLegendLightBlue;
        this.defaultGroundProfileStyle = 'solid';
        break;
      case 10: // Terrace ground surface
        this.defaultGroundProfileColor = profileSymbolsAndLabels.netsLegendGreen;
        this.defaultGroundProfileStyle = 'short-dash-dot';
        break;
      default:
        this.defaultGroundProfileColor = 'blue';
        this.defaultGroundProfileStyle = 'solid';
        break;
    }
  }

  /** events */
  public newMaxExtent: ((newExtent: Extent) => void) | undefined = undefined;

  /** properties */
  public get designProfileLayer() {
    const id = `${ProfileKeyCode[this.profileKey]}_design_profile`;
    const title = `${ProfileKeyCode[this.profileKey]} design profile`;

    if (!this._designProfileLayer) {
      const layer = this._profileView.map.findLayerById(id);
      if (layer) {
        this._designProfileLayer = layer as GraphicsLayer;
      } else {
        this._designProfileLayer = new GraphicsLayer({ id, title });
        this._profileView.map.add(this._designProfileLayer);
      }
    }

    return this._designProfileLayer;
  }

  public get groundProfileLayer() {
    const id = `${this.profileKey}_ground_profile`;
    const title = `${this.profileKey} ground profile`;

    if (!this._groundProfileLayer) {
      const layer = this._profileView.map.findLayerById(id);
      if (layer) {
        this._groundProfileLayer = layer as GraphicsLayer;
      } else {
        this._groundProfileLayer = new GraphicsLayer({ id, title });
        this._profileView.map.add(this._groundProfileLayer);
      }
    }

    return this._groundProfileLayer;
  }

  public get curvedProfileLayer() {
    const id = `${this.profileKey}_curved_profile`;
    const title = `${this.profileKey} curved profile`;

    if (!this._curvedProfileLayer) {
      const layer = this._profileView.map.findLayerById(id);
      if (layer) {
        this._curvedProfileLayer = layer as GraphicsLayer;
      } else {
        this._curvedProfileLayer = new GraphicsLayer({ id, title });
        this._profileView.map.add(this._curvedProfileLayer);
      }
    }

    return this._curvedProfileLayer;
  }

  /** methods */
  protected mapRound(input: number): number {
    return Math.round((input + Number.EPSILON) * 100) / 100;
  }

  /** actions */
  @action
  async insertVertex(newVertex: XyzPoint, vertexIndex: number, alignmentObject) {
    if (this.designVertices.length <= vertexIndex) {
      if (alignmentObject) {
        this.initData(alignmentObject);
      }
    }

    this.designVertices.splice(vertexIndex, 0, [newVertex.getZ(), newVertex.getX(), newVertex.getY()]);

    // if (vertexIndex === 0)
    const totalDist = await this.batchInterpolateSurface(this.designVertices, this.groundProfilePoints, false);
    this.drawProfileGraphics(totalDist, this.groundProfilePoints, this.groundProfileLayer, this.defaultGroundProfileColor);
  }

  @action
  async modifyVertex(newVertex: any, vertexIndex: any, alignmentObject?) {
    if (this.designVertices.length <= vertexIndex) {
      if (alignmentObject) {
        this.initData(alignmentObject);
        if (this.designVertices.length <= vertexIndex) return;
      }
    }

    // console.log('A vertex is moved in the vertex collection: ');
    // console.log(this.profileVertices);
    this.profileBatchDrawQueue.push([vertexIndex, newVertex.getZ(), newVertex.getX(), newVertex.getY()]);

    if (this.profileBatchDrawQueue.length === 1) {
      while (this.profileBatchDrawQueue.length > 0) {
        this.minElevation = 1000000;
        this.maxElevation = -1000;

        const peek = this.profileBatchDrawQueue[0];
        const [modifyIndex, ...theVertex] = peek;
        this.designVertices[modifyIndex] = theVertex;

        const totalDist = await this.batchInterpolateSurface();
        this.drawProfileGraphics(totalDist);

        this.profileBatchDrawQueue.shift();
      }
    }
  }

  @action
  async deleteVertex(info: any) {
    if (info.alignmentObject && info.alignmentObject.alignmentStations.length < 3) {
      this.curvedProfileLayer.removeAll();
    }
    if (this.designVertices.length <= info.index) {
      if (info.alignmentObject) {
        this.initData(info.alignmentObject);
        const totalDist = await this.batchInterpolateSurface();
        this.drawProfileGraphics(totalDist);
      }
    } else {
      this.designVertices.splice(info.index, 1);
      if (this.designVertices.length > 0) {
        const totalDist = await this.batchInterpolateSurface();
        this.drawProfileGraphics(totalDist);
      }
    }
  }

  reset() {
    this.groundProfileLayer.removeAll();
    this.curvedProfileLayer.removeAll();

    this.groundProfilePoints.length = 0;
    this.designVertices.length = 0;
    this.designVertexQueue.length = 0;

    this.minElevation = 1000000;
    this.maxElevation = -1000;
  }

  softProfileReset() {    // Only necessary for waterway and terrace for now
    this.groundProfileLayer.removeAll();
    this.minElevation = 1000000;
    this.maxElevation = -1000;
    this.ymax = undefined;
    this.ymin = undefined;
  }

  /** external data operations */
  initData(tableObject: any) {
    if (((designStore.currentSurface.surfaceDefClassName === 'survey' && !ElevationInterpolater.Current) || !tableObject)
      // && !tableObject.waterwayStations
    ) return;

    if (this.designVertices) this.designVertices.length = 0;
    if (tableObject.alignmentStations) {
      this.beginningStation = tableObject.beginningStationDis;
      const is2dAlignment = tableObject.alignmentStations.some(station => station.z == 0);
      if (!is2dAlignment) {
        this.designVertices = tableObject.alignmentStations.map((station) => {
          return [+station.z, +station.x, +station.y];
        });
      } else {
        this.designVertices = [];
      }
    } else if (tableObject.crossSectionPoints) {
      this.designVertices = tableObject.crossSectionPoints.map((point) => {
        return [+point.originalElevation, +point.x, +point.y];
      });
    } else if (tableObject.waterwayDesignStations) {
      this.designVertices = tableObject.waterwayDesignStations.map((station) => {
        return [+station.z, +station.x, +station.y];
      });
    }
  }

  mapDesignVertices(data) {
    this.designVertices = data.map((station) => {
      if (this.componentType !== 'alignment') station = station.inputPoint;
      return [+station.z, +station.x, +station.y];
    });
  }

  mapGroundProfilePoints(data) {
    this.groundProfilePoints = data.map((station) => {
      return [+station.distance, +station.inputPoint.z];
    })
  }

  drawDesignProfile() {
    if (this.designVertices && this.designVertices.length > 1) this.batchDrawDesignProfile();
  }

  async drawGroundProfile(shiftProfileValue?) {
    if (designStore.currentSurface.surfaceDefClassName != 'dem') {
      if (!ElevationInterpolater.Current) return;
    }

    // console.log(`start to draw ${ProfileKeyCode[this.profileKey]}`);
    if (this.designVertices && this.designVertices.length > 1) {
      const totalDist = await this.batchInterpolateSurface(this.designVertices, this.groundProfilePoints, true);
      if (shiftProfileValue) {
        this.groundProfilePoints.forEach(point => {
          point[0] += shiftProfileValue
        })
      }
      this.drawProfileGraphics(totalDist, this.groundProfilePoints, this.groundProfileLayer, this.defaultGroundProfileColor);
    }
  }


  // #temp-fix start
  drawOneTimeGroundProfile(userPoints: any[], beginningStation) {
    this.beginningStation = beginningStation;
    this.designVertices = userPoints.map((point) => {
      return [point.z, point.x, point.y];
    });

    this.drawGroundProfile();
  }

  // #temp-fix end

  drawCurveDirect(curveData: any) {
    if (curveData) {
      let dist = 0;
      if (this.curvedProfilePoints) this.curvedProfilePoints.length = 0;
      this.curvedProfilePoints = curveData.map((r, i, arr) => {
        if (i > 0) {
          const p1 = new XyzPoint(arr[i - 1].x, arr[i - 1].y, arr[i - 1].z);
          const p2 = new XyzPoint(arr[i].x, arr[i].y, arr[i].z);
          dist += getDistance2D(p1, p2);
        }
        if (r.z > this.maxElevation) this.maxElevation = r.z;
        if (r.z < this.minElevation) this.minElevation = r.z;

        return [this.mapRound(dist), this.mapRound(r.z), r.x, r.y];
      });

      this.drawProfileGraphics(dist, this.curvedProfilePoints, this.curvedProfileLayer, this.defaultCurvedProfileColor);

      this.groundProfileLayer.visible = false;
    }
  }

  async drawCurvedGroundProfile(curveData: any) {
    if (curveData && (ElevationInterpolater.Current || designStore.currentSurface.surfaceDefClassName === 'dem')) {
      if (this.curveGroundProfilePoints) this.curveGroundProfilePoints.length = 0;
      this.curveVertices = curveData.map((r) => {
        return [r.z, r.x, r.y];
      });

      const totalDist = await this.batchInterpolateSurface(this.curveVertices, this.curveGroundProfilePoints);
      this.drawProfileGraphics(
        totalDist,
        this.curveGroundProfilePoints,
        this.curvedProfileLayer,
        this.defaultCurvedProfileColor
      );

      this.groundProfileLayer.visible = false;
      // if (designStore && this.componentType === 'alignment') designStore.autoProfileZoom = true;  
    }
  }

  /** internal data operations */
  private batchDrawDesignProfile() {
    if (this.designProfilePoints) this.designProfilePoints.length = 0;
    let dist = 0;

    this.designProfilePoints = this.designVertices.map((r, i, arr) => {
      if (i > 0) {
        const p1 = new XyzPoint(arr[i - 1][1], arr[i - 1][2], arr[i - 1][0]);
        const p2 = new XyzPoint(arr[i][1], arr[i][2], arr[i][0]);
        dist += getDistance2D(p1, p2);

        if (r[0] > this.maxElevation) this.maxElevation = r[0];
        if (r[0] < this.minElevation) this.minElevation = r[0];
      }

      return [this.mapRound(dist), this.mapRound(r[0]), r[1], r[2]];
    });

    this.drawProfileGraphics(dist, this.designProfilePoints, this.designProfileLayer, this.defaultDesignProfileColor);

    this.groundProfileLayer.visible = false;
  }

  private async batchInterpolateSurface(
    vertices: number[][] = this.designVertices,
    points: number[][] = this.groundProfilePoints,
    willInterpolateVertex = false,
  ): Promise<number> {
    let dataZ = vertices[0][0];
    const dataX = vertices[0][1];
    const dataY = vertices[0][2];

    if (willInterpolateVertex && !dataZ) {
      const x = dataX;
      const y = dataY;
      const dataPoint = new Point({ x, y, spatialReference: this.dataSpatialReference });
      const mapPoint = esriProjection.project(dataPoint as Geometry, this.mapSpatialReference!);
      const z = designStore.currentSurface.surfaceDefClassName === 'survey' ? ElevationInterpolater.Current?.Interpolate(dataX, dataY, mapPoint as Point) : dataZ;
      if (z) dataZ = z;
    }

    points.length = 0;
    points.push([0, dataZ, dataX, dataY]);

    let totalDist = 0;
    for (let index = 1; index < vertices.length; index++) {
      const endVertex = vertices[index];
      let z = endVertex[0];

      if (willInterpolateVertex && !z) {
        const x = endVertex[1];
        const y = endVertex[2];
        const dataPoint = new Point({ x, y, spatialReference: this.dataSpatialReference });
        const mapPoint = esriProjection.project(dataPoint as Geometry, this.mapSpatialReference!);
        const elevation = designStore.currentSurface.surfaceDefClassName === 'survey' ? ElevationInterpolater.Current?.Interpolate(dataX, dataY, mapPoint as Point) : z;
        if (elevation) z = elevation;
      }

      const endPoint = new XyzPoint(endVertex[1], endVertex[2], z);

      const len = points.length;
      const pnt = points[len - 1];
      const beginPoint = new XyzPoint(pnt[2], pnt[3], pnt[1]);

      const [minZ, maxZ, dist] = await this.interpolateProfileBetween2Vertices(
        beginPoint,
        endPoint,
        undefined,
        this.dataSpatialReference!,
        this.mapSpatialReference,
        points[len - 1][0],
        points,
        2
      );
      if (minZ < this.minElevation) this.minElevation = minZ;
      if (maxZ > this.maxElevation) this.maxElevation = maxZ;

      totalDist = dist;
    }

    return totalDist;
    // this.drawProfileGraphics(totalDist);
    // console.log(`ended drawing ${ProfileKeyCode[this.profileKey]}`);
  }

  async interpolateSurfaceNewVertex(dataZ: number, dataX: number, dataY: number, keyCode) {
    let totalDist = 0;
    if (this.groundProfilePoints.length === 0) {
      this.groundProfilePoints.push([0, dataZ, dataX, dataY]);
      this.designVertices.push([dataZ, dataX, dataY]);
    } else {
      this.designVertices.push([dataZ, dataX, dataY]);
      this.designVertexQueue.push([dataZ, dataX, dataY]);

      if (this.designVertexQueue.length > 0) {
        while (this.designVertexQueue.length > 0) {
          const endVertex = this.designVertexQueue[0];
          const endPoint = new XyzPoint(endVertex[1], endVertex[2], endVertex[0]);

          const len = this.groundProfilePoints.length;
          const pnt = this.groundProfilePoints[len - 1];
          const beginPoint = new XyzPoint(pnt[2], pnt[3], pnt[1]);

          const [minZ, maxZ, dist] = await this.interpolateProfileBetween2Vertices(
            beginPoint,
            endPoint,
            keyCode,
            this.dataSpatialReference!,
            this.mapSpatialReference,
            this.groundProfilePoints[len - 1][0],
            undefined,
            2
          );
          if (minZ < this.minElevation) this.minElevation = minZ;
          if (maxZ > this.maxElevation) this.maxElevation = maxZ;
          totalDist = dist;

          this.designVertexQueue.shift();

          // draw profiles Begin
          this.drawProfileGraphics(totalDist, this.groundProfilePoints, this.groundProfileLayer, this.defaultGroundProfileColor);
        }
      }
    }
  }

  private calculateBestYRange() {
    const increments = [5, 10, 20, 50, 100, 200, 400];
    for (const increment of increments) {
      const paddedMin = this.minElevation - increment;
      const paddedMax = this.maxElevation + increment;
  
      const gridYMin = Math.floor(paddedMin / increment) * increment;
      const gridYMax = Math.ceil(paddedMax / increment) * increment;
  
      const numGridLines = Math.round((gridYMax - gridYMin) / increment) + 1;
  
      if (numGridLines < 10) {
        return {
          gridYMin,
          gridYMax,
          padding: 0
        };
      }
    }

    return {
      gridYMin: this.minElevation,
      gridYMax: this.maxElevation,
      padding: 50
    }
  }

  public drawProfileGraphics(
    totalDist: number,
    profilePoints = this.groundProfilePoints,
    graphicsLayer: GraphicsLayer = this.groundProfileLayer,
    color = this.defaultGroundProfileColor,
    style = this.defaultGroundProfileStyle
  ) {
    const mapView = EngineeringMapService.instance?.mapView;
    if (mapView) mapView.popup.visible = false;

    if (this.componentType === "alignment" && this.beginningStation > 0) {
      totalDist = totalDist + this.beginningStation;
    }
    const isThreeCrossSection = this.componentType === "threeCrossSection1" || this.componentType === "threeCrossSection2" || this.componentType === "threeCrossSection3";
    const needsVerticalStretch = this.componentType === "terrace" || this.componentType === "waterway" || this.componentType === "UGO";

    if (this.ymax) this.maxElevation = this.ymax;
    if (this.ymin) this.minElevation = this.ymin;    
    
    // calc extent view
    const w = this._profileView?.extent.width;
    const h = this._profileView?.extent.height;

    if (w && h) {
      // calculate the extent
      const [finalYFactor, finalXFactor] = this.calculateExtent(totalDist, isThreeCrossSection, needsVerticalStretch);
      
      // Draw graphics
      let path;
      if (this.componentType === 'alignment') {
        path = profilePoints.map((r) => [r[0] + this.beginningStation, r[1] * AppConstants.profileStretchFactor]);
      } else {
        path = finalXFactor ? profilePoints.map((r) => [r[0] * finalXFactor, r[1] * finalYFactor]) : profilePoints.map((r) => [r[0], r[1] * finalYFactor]);
      }
      const geometry = new Polyline({
        spatialReference: this._profileView?.spatialReference,
        paths: [path],
      });

      const profileGraphic = new Graphic({
        geometry,
        symbol: {
          type: 'simple-line',
          color,
          width: this.defaultGroundProfileWidth,
          style
        } as any,
      });

      graphicsLayer.visible = true;
      graphicsLayer.removeAll();
      graphicsLayer.add(profileGraphic);
    }
    this.h_grid!.visible = true;
    this.v_grid!.visible = true;
  }

  private calculateExtent(totalDist, isThreeCrossSection, needsVerticalStretch) {
    const xFactor = totalDist > 400 ? 100 : 10;
    const dataXmin = isThreeCrossSection ? this.groundProfilePoints[0][0] - this.tcsBufferWidth : 0;
    const dataXmax = isThreeCrossSection  ? (this.groundProfilePoints[this.groundProfilePoints.length - 1][0] + this.tcsBufferWidth) : totalDist;
    const xmax = Math.ceil(dataXmax / xFactor) * xFactor;
    let finalYFactor: number = AppConstants.profileStretchFactor;
    let finalXFactor;

    if (needsVerticalStretch) {
      const yGridResult = this.calculateBestYRange();
      const yRange = yGridResult.gridYMax - yGridResult.gridYMin;
      const testYFactor = Math.floor((0.75 * (totalDist / yRange)) / 10) * 10;
      if (testYFactor !== 0) finalYFactor = testYFactor;

      const currentExt = new Extent({
        xmin: 0,
        ymin: (yGridResult.gridYMin * finalYFactor) - yGridResult.padding,
        xmax,
        ymax: (yGridResult.gridYMax * finalYFactor) + yGridResult.padding,
        spatialReference: this._profileView?.spatialReference,
      });
      this._profileView.extent = currentExt;

      // Get profile width that fits the Y extents
      const profileMapUnitsWidth = this._profileView.extent.xmax + Math.abs(this._profileView.extent.xmin);
      const ratio = profileMapUnitsWidth / dataXmax;

      finalXFactor = this.setStretchFactors(finalYFactor, ratio);

      this.maxExtent = new Extent({
        xmin: -Math.floor(currentExt.xmax / 10),
        ymin: currentExt.ymin,
        xmax: profileMapUnitsWidth,
        ymax: currentExt.ymax,
        spatialReference: this._profileView?.spatialReference,
      });
    } else {
      let initialYMax;
      let initialYMin;
      let ymin;
      let ymax;

      if (isThreeCrossSection) {
        ymin = (Math.floor(this.minElevation) * finalYFactor) - 50;
        ymax = (Math.ceil(this.maxElevation) * finalYFactor) + 50;
      } else {
        const yFactor = (this.maxElevation - this.minElevation) > 400 ? 100 : 10;
        initialYMin = this.minElevation / yFactor;
        initialYMax = this.maxElevation / yFactor
        ymin = Math.floor(initialYMin) * yFactor * AppConstants.profileStretchFactor;
        ymax = Math.ceil(initialYMax) * yFactor * AppConstants.profileStretchFactor;
      }
      const ext = new Extent({
        xmin: dataXmin,
        ymin,
        xmax,
        ymax,
        spatialReference: this._profileView?.spatialReference,
      });
      const currentExt = ext;
      if ((designStore.autoProfileZoom && this.componentType === 'alignment') || this.componentType !== 'alignment') this._profileView.extent = ext;
      
      // Get profile width that fits the Y extents
      const profileMapUnitsWidth = (this._profileView.extent.xmax + Math.abs(this._profileView.extent.xmin) - 50);
      const ratio = profileMapUnitsWidth / totalDist;

      finalXFactor = this.setStretchFactors(finalYFactor, ratio);

      if (this.componentType !== 'alignment' && this.componentType !== 'singleCrossSection') {
        currentExt.xmax = isThreeCrossSection ? dataXmax * finalXFactor : profileMapUnitsWidth;
        currentExt.xmin = isThreeCrossSection ? (dataXmin - 50) * finalXFactor : -100;
      }
      
      this.maxExtent = new Extent({
        xmin: currentExt.xmin,
        ymin: currentExt.ymin,
        xmax: currentExt.xmax,
        ymax: currentExt.ymax,
        spatialReference: this._profileView?.spatialReference,
      });  
    }

    if (this.maxExtent) this.raiseNewMaxExtentEvent(this.maxExtent);
    return [finalYFactor, finalXFactor];
  }

  private setStretchFactors(currentYFactor, ratio) {
    switch (true) {
      case this.componentType === 'waterway' || this.componentType === 'UGO' || this.componentType === "terrace":
        designStore.commonHorizontalStretchFactor =  designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : ratio;
        designStore.commonVerticalStretchFactor = designStore.commonVerticalStretchFactor ? designStore.commonVerticalStretchFactor : currentYFactor;
        return designStore.commonHorizontalStretchFactor;
      case this.componentType === 'threeCrossSection1':
        designStore.tcs1HorizontalStretchFactor = designStore.tcs1HorizontalStretchFactor ? designStore.tcs1HorizontalStretchFactor : ratio;
        return designStore.tcs1HorizontalStretchFactor;
      case this.componentType === 'threeCrossSection2':
        designStore.tcs2HorizontalStretchFactor = designStore.tcs2HorizontalStretchFactor ? designStore.tcs2HorizontalStretchFactor : ratio;
        return designStore.tcs2HorizontalStretchFactor;
      case this.componentType === 'threeCrossSection3':
        designStore.tcs3HorizontalStretchFactor = designStore.tcs3HorizontalStretchFactor ? designStore.tcs3HorizontalStretchFactor : ratio;
        return designStore.tcs3HorizontalStretchFactor;
      default:
        return undefined;
    }
  }

  private raiseNewMaxExtentEvent(extent: Extent) {
    if (this.newMaxExtent) this.newMaxExtent(extent);
  }

  private async interpolateProfileBetween2Vertices(
    begin: XyzPoint,
    end: XyzPoint,
    keyCode: number | undefined,
    srData: SpatialReference,
    srMap: SpatialReference | undefined,
    baseDist = 0,
    points: number[][] = this.groundProfilePoints,
    segment = 2
  ) {

    // #temp-fix start
    if (designStore.currentSurface.surfaceDefClassName !== 'survey') {
      segment = 2;
    }
    // #temp-fix end

    let maxZ = Math.max(begin.getZ(), end.getZ());
    let minZ = Math.min(begin.getZ(), end.getZ());

    const dist = getDistance2D(begin, end);
    const totalDist = baseDist + dist;
    if (dist < segment) {
      points.push([this.mapRound(totalDist), this.mapRound(end.getZ()), end.getX(), end.getY()]);
    } else {
      for (let index = 1; index < dist / segment; index++) {
        const deltaDist = index * segment;
        const x = begin.getX() + (deltaDist / dist) * (end.getX() - begin.getX());
        const y = begin.getY() + (deltaDist / dist) * (end.getY() - begin.getY());

        const dataPoint = new Point({ x, y, spatialReference: srData });
        const mapPoint = esriProjection.project(dataPoint as Geometry, srMap!);
        let z;

        /* if current profile is Original (keyCode 2), we need to make sure we are interpolating on the Original surface. The default is to interpolate on the 
        'active' surface, which is always the Design surface if it exists*/
        if (designStore.currentSurface.surfaceDefClassName === 'survey' && this.componentType === 'alignment') {
          z = ElevationInterpolater.Current?.Interpolate(x, y, mapPoint as Point);
        } else {
            const beginDataPoint = new Point({ x: begin.x, y: begin.y, spatialReference: srData });
            const endDataPoint = new Point({ x: end.x, y: end.y, spatialReference: srData });
            let zB;
            let bA;
            let zC;
            const bC = esriGeometryEngine.distance(beginDataPoint, endDataPoint, 'feet');

            if (begin.z > end.z) {
              zB = end.z;
              zC = begin.z;
              bA = esriGeometryEngine.distance(dataPoint, endDataPoint, 'feet');
            } else if (end.z > begin.z) {
              zB = begin.z;
              zC = end.z;
              bA = esriGeometryEngine.distance(dataPoint, beginDataPoint, 'feet');
            } else if (begin.z === end.z) {
              z = begin.z;
            }

          if (zB && bA && zC && bC) {
            z = ((zC - zB) / bC) * bA + zB;
          }
        }

        if (z) points.push([this.mapRound(baseDist + deltaDist), this.mapRound(z), x, y]);

        if (z > maxZ) maxZ = z;
        if (z < minZ) minZ = z;
      }

      points.push([this.mapRound(totalDist), this.mapRound(end.getZ()), end.getX(), end.getY()]);
    }

    return [minZ, maxZ, totalDist];
  }

  /** clean up and dispose */

  private deepRemoveLayer(lyr: Layer | undefined) {
    if (lyr) {
      this._profileView?.map?.remove(lyr);
      lyr.destroy();
    }
  }

  cleanUpMap() {
    this.deepRemoveLayer(this._designProfileLayer);
    this._designProfileLayer = void 0;

    this.deepRemoveLayer(this._groundProfileLayer);
    this._groundProfileLayer = void 0;

    this.deepRemoveLayer(this._curvedProfileLayer);
    this._curvedProfileLayer = void 0;
  }

  Dispose() {
    this.cleanUpMap();
  }
}

export { DynamicProfileClass };
